/**
 * Custom hook for VehicleCard component
 * Handles state and API calls for vehicle details
 */

import { useEffect, useState, useCallback } from 'react';
import api from '@/services/api/client';
import {
  VehicleData,
  CarInfoData,
  VehicleDetailsResponse,
  AvailableDevice,
  TabId,
  OdometerData,
  VehiclePickupPlacement,
} from '@/types/vehicle';
import vehiclesService from '@/services/vehicles/vehiclesService';
import type { PlacementFormState } from '@/components/vehicle/VehiclePlacementTab';
import type { ChargingConfigFormState } from '@/components/vehicle/VehicleChargingTab';

/** Samma normalisering som backend (endast A–Z och siffror). */
const normReg = (s: string | null | undefined) =>
  (s ?? '').trim().toUpperCase().replace(/[^A-Z0-9]/g, '');

interface UseVehicleCardProps {
  registrationNumber: string;
  vehicleId?: number;
}

interface VehicleCategoryOption {
  id: number;
  name: string;
  topNodeOrganizationId: string;
}

interface VehicleAlternativeFuelOption {
  id: number;
  name: string;
  topNodeOrganizationId: string;
}

interface VehicleUsageStatusOption {
  id: number;
  name: string;
  topNodeOrganizationId: string;
}

export const useVehicleCard = ({ registrationNumber, vehicleId }: UseVehicleCardProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [vehicle, setVehicle] = useState<VehicleData | null>(null);
  const [carInfo, setCarInfo] = useState<CarInfoData | null>(null);
  const [odometer, setOdometer] = useState<OdometerData | null>(null);
  const [meta, setMeta] = useState<{ fromCache: boolean; cacheAge: number | null }>({ fromCache: false, cacheAge: null });
  const [activeTab, setActiveTab] = useState<TabId>('general');
  const [pickupPlacements, setPickupPlacements] = useState<VehiclePickupPlacement[]>([]);
  const [pickupPlacementsLoaded, setPickupPlacementsLoaded] = useState(false);

  // Device assignment states
  const [availableDevices, setAvailableDevices] = useState<AvailableDevice[]>([]);
  const [deviceSearch, setDeviceSearch] = useState('');
  const [isLoadingDevices, setIsLoadingDevices] = useState(false);
  const [showDeviceDropdown, setShowDeviceDropdown] = useState(false);

  // Pending changes
  const [pendingImei, setPendingImei] = useState<string | null | undefined>(undefined);
  const [pendingNotes, setPendingNotes] = useState<string | undefined>(undefined);
  /** När satt: vald organisations-id (sträng); sparas som organization_id vid Spara */
  const [pendingOrganizationId, setPendingOrganizationId] = useState<string | undefined>(undefined);
  const [vehicleCategories, setVehicleCategories] = useState<VehicleCategoryOption[]>([]);
  const [isLoadingVehicleCategories, setIsLoadingVehicleCategories] = useState(false);
  const [pendingVehicleCategoryId, setPendingVehicleCategoryId] = useState<string | undefined>(undefined);
  const [alternativeFuels, setAlternativeFuels] = useState<VehicleAlternativeFuelOption[]>([]);
  const [isLoadingAlternativeFuels, setIsLoadingAlternativeFuels] = useState(false);
  const [pendingAlternativeFuelId, setPendingAlternativeFuelId] = useState<string | undefined>(undefined);
  const [usageStatuses, setUsageStatuses] = useState<VehicleUsageStatusOption[]>([]);
  const [isLoadingUsageStatuses, setIsLoadingUsageStatuses] = useState(false);
  const [pendingUsageStatusId, setPendingUsageStatusId] = useState<string | undefined>(undefined);
  const [pendingBusinessResponsibilityId, setPendingBusinessResponsibilityId] = useState<
    string | undefined
  >(undefined);
  /** När satt: ersätter fordon (regnr); tom sträng = rensa i DB */
  const [pendingReplacesVehicleRegistration, setPendingReplacesVehicleRegistration] = useState<
    string | undefined
  >(undefined);
  const [pendingReferenceNumber, setPendingReferenceNumber] = useState<string | undefined>(undefined);
  const [pendingPlacement, setPendingPlacement] = useState<PlacementFormState | undefined>(undefined);
  const [pendingCharging, setPendingCharging] = useState<ChargingConfigFormState | undefined>(undefined);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  /** Väntar på bekräftelse vid org-flytt (samma toppnod eller ny toppnod) */
  const [orgMovePending, setOrgMovePending] = useState<{
    targetOrganizationId: number;
    isCrossTop: boolean;
  } | null>(null);

  // Odometer editing states
  const [isEditingOdometer, setIsEditingOdometer] = useState(false);
  const [pendingInitialOdometerKm, setPendingInitialOdometerKm] = useState<number | null>(null);
  const [pendingCurrentOdometerKm, setPendingCurrentOdometerKm] = useState<number | null>(null);
  const [isSavingOdometer, setIsSavingOdometer] = useState(false);

  // Expanded categories for specifications
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  // flinkey (digital nyckel)
  const [flinkeyBoxIdInput, setFlinkeyBoxIdInput] = useState('');
  const [isSavingFlinkey, setIsSavingFlinkey] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignUserId, setAssignUserId] = useState('');
  const [assignValidFrom, setAssignValidFrom] = useState('');
  const [assignValidUntil, setAssignValidUntil] = useState('');
  const [isAssigningFlinkey, setIsAssigningFlinkey] = useState(false);

  // flinkey box (från flinkey_boxes-tabellen)
  interface FlinkeyBoxOption {
    id: number;
    product_id: string;
    serial_number: string;
    box_type: string | null;
    /** Om boxen redan sitter på ett fordon (kan vara annat än aktuellt kort) */
    linked_vehicle_id?: number | null;
    linked_vehicle_registration?: string | null;
  }
  const [linkedFlinkeyBox, setLinkedFlinkeyBox] = useState<FlinkeyBoxOption | null>(null);
  const [availableFlinkeyBoxes, setAvailableFlinkeyBoxes] = useState<FlinkeyBoxOption[]>([]);
  const [isLoadingFlinkeyBoxes, setIsLoadingFlinkeyBoxes] = useState(false);
  const [flinkeyListLoadError, setFlinkeyListLoadError] = useState<string | null>(null);
  const [showFlinkeyBoxDropdown, setShowFlinkeyBoxDropdown] = useState(false);
  const [flinkeyBoxSearch, setFlinkeyBoxSearch] = useState('');

  const placementFromVehicle = (v: VehicleData): PlacementFormState => ({
    address: v.placementAddress ?? '',
    postalCode: v.placementPostalCode ?? '',
    city: v.placementCity ?? '',
    latitude: v.placementLatitude ?? null,
    longitude: v.placementLongitude ?? null,
  });

  const placementEquals = (a: PlacementFormState, b: PlacementFormState) =>
    a.address.trim() === b.address.trim() &&
    a.postalCode.trim() === b.postalCode.trim() &&
    a.city.trim() === b.city.trim() &&
    a.latitude === b.latitude &&
    a.longitude === b.longitude;

  const chargingFromVehicle = (v: VehicleData): ChargingConfigFormState => ({
    batteryCapacityKwh: v.batteryCapacityKwh ?? null,
    chargingEfficiency: v.chargingEfficiency ?? null,
    homeLatitude: v.homeLatitude ?? null,
    homeLongitude: v.homeLongitude ?? null,
    homeRadiusM: v.homeRadiusM ?? null,
    homeAddress: v.homeAddress ?? null,
    workLatitude: v.workLatitude ?? null,
    workLongitude: v.workLongitude ?? null,
    workRadiusM: v.workRadiusM ?? null,
    workAddress: v.workAddress ?? null,
  });

  const chargingEquals = (a: ChargingConfigFormState, b: ChargingConfigFormState) =>
    a.batteryCapacityKwh === b.batteryCapacityKwh &&
    a.chargingEfficiency === b.chargingEfficiency &&
    a.homeLatitude === b.homeLatitude &&
    a.homeLongitude === b.homeLongitude &&
    a.homeRadiusM === b.homeRadiusM &&
    a.homeAddress === b.homeAddress &&
    a.workLatitude === b.workLatitude &&
    a.workLongitude === b.workLongitude &&
    a.workRadiusM === b.workRadiusM &&
    a.workAddress === b.workAddress;

  const hasChanges =
    pendingImei !== undefined ||
    pendingNotes !== undefined ||
    (pendingOrganizationId !== undefined &&
      pendingOrganizationId !== (vehicle?.organizationId ?? '')) ||
    (pendingVehicleCategoryId !== undefined &&
      pendingVehicleCategoryId !== String(vehicle?.vehicleCategoryId ?? '')) ||
    (pendingAlternativeFuelId !== undefined &&
      pendingAlternativeFuelId !== String(vehicle?.alternativeFuelId ?? '')) ||
    (pendingUsageStatusId !== undefined &&
      pendingUsageStatusId !== String(vehicle?.usageStatusId ?? '')) ||
    (pendingBusinessResponsibilityId !== undefined &&
      pendingBusinessResponsibilityId !== String(vehicle?.businessResponsibilityId ?? '')) ||
    (pendingReplacesVehicleRegistration !== undefined &&
      normReg(pendingReplacesVehicleRegistration) !==
        normReg(vehicle?.replacesVehicleRegistration ?? null)) ||
    (pendingReferenceNumber !== undefined &&
      (pendingReferenceNumber ?? '').trim() !== (vehicle?.referenceNumber ?? '').trim()) ||
    (pendingPlacement !== undefined &&
      vehicle != null &&
      !placementEquals(pendingPlacement, placementFromVehicle(vehicle))) ||
    (pendingCharging !== undefined &&
      vehicle != null &&
      !chargingEquals(pendingCharging, chargingFromVehicle(vehicle)));

  const fetchDetails = useCallback(async (refresh: boolean = false) => {
    if (refresh) {
      setIsRefreshing(true);
    } else {
      setIsLoading(true);
    }
    setError(null);

    try {
      const qs = refresh ? '?refresh=true' : '';
      const url =
        vehicleId != null && Number.isFinite(vehicleId) && vehicleId > 0
          ? `/api/vehicles/by-id/${encodeURIComponent(String(vehicleId))}/details${qs}`
          : `/api/vehicles/details/${encodeURIComponent(registrationNumber)}${qs}`;
      const response = await api.get<VehicleDetailsResponse>(url);

      if (response.data.success) {
        setVehicle(response.data.data.vehicle);
        setCarInfo(response.data.data.carInfo);
        setOdometer(response.data.data.odometer || null);
        setMeta({
          fromCache: response.data.data._meta.carInfoFromCache,
          cacheAge: response.data.data._meta.carInfoCacheAge
        });
      } else {
        setError('Kunde inte hämta fordonsdata');
      }
    } catch (err: any) {
      console.error('Error fetching vehicle details:', err);
      setError(err.response?.data?.error || 'Ett fel uppstod');
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [registrationNumber, vehicleId]);

  useEffect(() => {
    fetchDetails();
  }, [fetchDetails]);

  useEffect(() => {
    const organizationId =
      pendingOrganizationId !== undefined
        ? pendingOrganizationId
        : String(vehicle?.organizationId ?? '');
    if (!organizationId) {
      setVehicleCategories([]);
      return;
    }

    let cancelled = false;
    const loadVehicleCategories = async () => {
      setIsLoadingVehicleCategories(true);
      try {
        const response = await api.get<{
          success: boolean;
          data: VehicleCategoryOption[];
        }>('/api/vehicles/categories', {
          params: { organizationId },
        });
        if (!cancelled) {
          setVehicleCategories(response.data.success ? response.data.data || [] : []);
        }
      } catch (err) {
        console.error('Error fetching vehicle categories:', err);
        if (!cancelled) setVehicleCategories([]);
      } finally {
        if (!cancelled) setIsLoadingVehicleCategories(false);
      }
    };

    loadVehicleCategories();
    return () => {
      cancelled = true;
    };
  }, [pendingOrganizationId, vehicle?.organizationId]);

  useEffect(() => {
    const organizationId =
      pendingOrganizationId !== undefined
        ? pendingOrganizationId
        : String(vehicle?.organizationId ?? '');
    if (!organizationId) {
      setAlternativeFuels([]);
      return;
    }

    let cancelled = false;
    const loadAlternativeFuels = async () => {
      setIsLoadingAlternativeFuels(true);
      try {
        const response = await api.get<{
          success: boolean;
          data: VehicleAlternativeFuelOption[];
        }>('/api/vehicles/alternative-fuels', {
          params: { organizationId },
        });
        if (!cancelled) {
          setAlternativeFuels(response.data.success ? response.data.data || [] : []);
        }
      } catch (err) {
        console.error('Error fetching alternative fuels:', err);
        if (!cancelled) setAlternativeFuels([]);
      } finally {
        if (!cancelled) setIsLoadingAlternativeFuels(false);
      }
    };

    loadAlternativeFuels();
    return () => {
      cancelled = true;
    };
  }, [pendingOrganizationId, vehicle?.organizationId]);

  useEffect(() => {
    const organizationId =
      pendingOrganizationId !== undefined
        ? pendingOrganizationId
        : String(vehicle?.organizationId ?? '');
    if (!organizationId) {
      setUsageStatuses([]);
      return;
    }

    let cancelled = false;
    const loadUsageStatuses = async () => {
      setIsLoadingUsageStatuses(true);
      try {
        const response = await api.get<{
          success: boolean;
          data: VehicleUsageStatusOption[];
        }>('/api/vehicles/usage-statuses', {
          params: { organizationId },
        });
        if (!cancelled) {
          setUsageStatuses(response.data.success ? response.data.data || [] : []);
        }
      } catch (err) {
        console.error('Error fetching usage statuses:', err);
        if (!cancelled) setUsageStatuses([]);
      } finally {
        if (!cancelled) setIsLoadingUsageStatuses(false);
      }
    };

    loadUsageStatuses();
    return () => {
      cancelled = true;
    };
  }, [pendingOrganizationId, vehicle?.organizationId]);

  const fetchAvailableDevices = useCallback(async (search: string = '') => {
    if (!vehicle?.id) return;
    
    setIsLoadingDevices(true);
    
    try {
      const response = await api.get(`/api/vehicles/${vehicle.id}/available-devices`, {
        params: { search }
      });
      
      if (response.data.success) {
        setAvailableDevices(response.data.data || []);
      }
    } catch (err: any) {
      console.error('Error fetching available devices:', err);
      setAvailableDevices([]);
    } finally {
      setIsLoadingDevices(false);
    }
  }, [vehicle?.id]);

  const selectDevice = (imei: string | null) => {
    setPendingImei(imei);
    setShowDeviceDropdown(false);
    setDeviceSearch('');
  };

  const resetChanges = () => {
    setPendingImei(undefined);
    setPendingNotes(undefined);
    setPendingOrganizationId(undefined);
    setPendingVehicleCategoryId(undefined);
    setPendingAlternativeFuelId(undefined);
    setPendingUsageStatusId(undefined);
    setPendingBusinessResponsibilityId(undefined);
    setPendingReplacesVehicleRegistration(undefined);
    setPendingReferenceNumber(undefined);
    setPendingPlacement(undefined);
    setPendingCharging(undefined);
    setSaveError(null);
    setOrgMovePending(null);
  };

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  const saveOdometer = async () => {
    if (!vehicle?.id) return;
    setIsSavingOdometer(true);
    setSaveError(null);
    try {
      const response = await api.put(`/api/vehicles/${vehicle.id}/odometer`, {
        initial_odometer_km: pendingInitialOdometerKm,
        current_odometer_km: pendingCurrentOdometerKm,
      });
      if (response.data.success) {
        setIsEditingOdometer(false);
        await fetchDetails();
      } else {
        setSaveError(response.data.error || 'Kunde inte spara mätarställning');
      }
    } catch (err: any) {
      setSaveError(err.response?.data?.error || err.message || 'Fel vid sparande');
    } finally {
      setIsSavingOdometer(false);
    }
  };

  const cancelOdometerEdit = () => {
    setIsEditingOdometer(false);
    setPendingInitialOdometerKm(null);
    setPendingCurrentOdometerKm(null);
  };

  const attachFlinkey = async () => {
    if (!vehicle?.id || !flinkeyBoxIdInput.trim()) return;
    setIsSavingFlinkey(true);
    setSaveError(null);
    try {
      const response = await api.post(`/api/vehicles/${vehicle.id}/flinkey`, {
        flinkeyBoxId: flinkeyBoxIdInput.trim(),
      });
      if (response.data.success) {
        setFlinkeyBoxIdInput('');
        await fetchDetails();
      } else {
        setSaveError(response.data.error || 'Kunde inte koppla flinkey');
      }
    } catch (err: any) {
      setSaveError(err.response?.data?.error || err.message || 'Kunde inte koppla flinkey');
    } finally {
      setIsSavingFlinkey(false);
    }
  };

  const detachFlinkey = async () => {
    if (!vehicle?.id) return;
    setIsSavingFlinkey(true);
    setSaveError(null);
    try {
      const response = await api.delete(`/api/vehicles/${vehicle.id}/flinkey`);
      if (response.data.success) {
        await fetchDetails();
      } else {
        setSaveError(response.data.error || 'Kunde inte ta bort flinkey');
      }
    } catch (err: any) {
      setSaveError(err.response?.data?.error || err.message || 'Kunde inte ta bort flinkey');
    } finally {
      setIsSavingFlinkey(false);
    }
  };

  // Hämtar Flinkey-box kopplad till fordonet + tillgängliga boxar i organisationen
  const fetchFlinkeyBox = useCallback(async () => {
    if (!vehicle?.id) return;
    setIsLoadingFlinkeyBoxes(true);
    setFlinkeyListLoadError(null);
    try {
      const res = await api.get(`/api/vehicles/${vehicle.id}/flinkey-box`);
      if (res.data.success) {
        setLinkedFlinkeyBox(res.data.data.linkedBox);
        setAvailableFlinkeyBoxes(res.data.data.availableBoxes ?? []);
      } else {
        setLinkedFlinkeyBox(null);
        setAvailableFlinkeyBoxes([]);
        setFlinkeyListLoadError(typeof res.data.error === 'string' ? res.data.error : '');
      }
    } catch (err: unknown) {
      console.error('Error fetching flinkey box:', err);
      setLinkedFlinkeyBox(null);
      setAvailableFlinkeyBoxes([]);
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
          : undefined;
      setFlinkeyListLoadError(typeof msg === 'string' && msg.trim() ? msg : '');
    } finally {
      setIsLoadingFlinkeyBoxes(false);
    }
  }, [vehicle?.id]);

  // Koppla en box till fordonet (via flinkey_boxes.vehicle_id)
  const linkFlinkeyBox = useCallback(async (boxId: number) => {
    if (!vehicle?.id) return;
    setIsSavingFlinkey(true);
    try {
      await api.post(`/api/admin/flinkey/boxes/${boxId}/vehicle`, { vehicleId: vehicle.id });
      await fetchFlinkeyBox();
    } catch (err: any) {
      setSaveError(err.response?.data?.error || err.message || 'Kunde inte koppla box');
    } finally {
      setIsSavingFlinkey(false);
    }
  }, [vehicle?.id, fetchFlinkeyBox]);

  // Koppla loss box från fordonet
  const unlinkFlinkeyBox = useCallback(async (boxId: number) => {
    if (!vehicle?.id) return;
    setIsSavingFlinkey(true);
    try {
      await api.delete(`/api/admin/flinkey/boxes/${boxId}/vehicle`);
      await fetchFlinkeyBox();
    } catch (err: any) {
      setSaveError(err.response?.data?.error || err.message || 'Kunde inte koppla loss box');
    } finally {
      setIsSavingFlinkey(false);
    }
  }, [vehicle?.id, fetchFlinkeyBox]);

  const assignFlinkey = async () => {
    if (!vehicle?.id || !assignUserId.trim()) return;
    setIsAssigningFlinkey(true);
    setSaveError(null);
    try {
      const response = await api.post('/api/flinkey/assign', {
        vehicleId: vehicle.id,
        userId: assignUserId.trim(),
        validFrom: assignValidFrom.trim() || undefined,
        validUntil: assignValidUntil.trim() || undefined,
      });
      if (response.data.success) {
        setShowAssignModal(false);
        setAssignUserId('');
        setAssignValidFrom('');
        setAssignValidUntil('');
      } else {
        setSaveError(response.data.error || 'Kunde inte tilldela');
      }
    } catch (err: any) {
      setSaveError(err.response?.data?.error || err.message || 'Kunde inte tilldela');
    } finally {
      setIsAssigningFlinkey(false);
    }
  };

  const cancelOrgMove = () => {
    setOrgMovePending(null);
  };

  const confirmOrgMove = async (
    note: string,
    options: { keepVehicleData: boolean; moveDevice: boolean }
  ) => {
    if (!vehicle?.id || !orgMovePending) return;
    setIsSaving(true);
    setSaveError(null);
    try {
      const useTransferApi = orgMovePending.isCrossTop || !options.keepVehicleData;
      await runVehicleSavePipeline({
        orgTransfer: useTransferApi
          ? {
              targetOrganizationId: orgMovePending.targetOrganizationId,
              note: note.trim() || undefined,
              keepVehicleData: options.keepVehicleData,
              moveDevice: orgMovePending.isCrossTop ? options.moveDevice : true,
            }
          : undefined,
      });
      setOrgMovePending(null);
    } catch (err: unknown) {
      console.error('Error saving cross-top transfer:', err);
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
          : undefined;
      setSaveError(
        typeof msg === 'string' && msg.trim()
          ? msg
          : err instanceof Error
            ? err.message
            : 'Ett fel uppstod vid överföring'
      );
    } finally {
      setIsSaving(false);
    }
  };

  const runVehicleSavePipeline = async (opts: {
    orgTransfer?: {
      targetOrganizationId: number;
      note?: string;
      keepVehicleData: boolean;
      moveDevice: boolean;
    };
  }) => {
    if (!vehicle?.id) throw new Error('Saknar fordon');

    let writeVehicleId = String(vehicle.id);

    if (opts.orgTransfer) {
      const tr = await api.post<{
        success: boolean;
        data?: { toVehicleId: number };
        error?: string;
      }>(`/api/vehicles/${vehicle.id}/transfer-cross-top`, {
        target_organization_id: opts.orgTransfer.targetOrganizationId,
        note: opts.orgTransfer.note,
        keep_vehicle_data: opts.orgTransfer.keepVehicleData,
        move_device: opts.orgTransfer.moveDevice,
      });
      if (!tr.data.success || tr.data.data?.toVehicleId == null) {
        throw new Error(tr.data.error || 'Kunde inte överföra fordon');
      }
      writeVehicleId = String(tr.data.data.toVehicleId);
      setPendingOrganizationId(undefined);
    }

    if (pendingImei !== undefined) {
      const response = await api.put(`/api/vehicles/${writeVehicleId}/device`, { imei: pendingImei });
      if (!response.data.success) {
        throw new Error(response.data.error || 'Kunde inte spara ändring');
      }
    }

    const vehicleBody: {
      notes?: string;
      organization_id?: number;
      replaces_vehicle_registration?: string | null;
      reference_number?: string | null;
      vehicle_category_id?: number | null;
      alternative_fuel_id?: number | null;
      usage_status_id?: number | null;
      business_responsibility_id?: number | null;
      placement_address?: string | null;
      placement_postal_code?: string | null;
      placement_city?: string | null;
      placement_latitude?: number | null;
      placement_longitude?: number | null;
      battery_capacity_kwh?: number | null;
      charging_efficiency?: number | null;
      home_latitude?: number | null;
      home_longitude?: number | null;
      home_radius_m?: number | null;
      home_address?: string | null;
      work_latitude?: number | null;
      work_longitude?: number | null;
      work_radius_m?: number | null;
      work_address?: string | null;
    } = {};
    if (pendingNotes !== undefined) {
      vehicleBody.notes = pendingNotes;
    }
    if (
      !opts.orgTransfer &&
      pendingOrganizationId !== undefined &&
      pendingOrganizationId !== (vehicle.organizationId ?? '')
    ) {
      const oid = parseInt(pendingOrganizationId, 10);
      if (Number.isNaN(oid) || oid <= 0) {
        throw new Error('Ogiltig organisation');
      }
      vehicleBody.organization_id = oid;
    }
    if (pendingReplacesVehicleRegistration !== undefined) {
      const n = normReg(pendingReplacesVehicleRegistration);
      vehicleBody.replaces_vehicle_registration = n === '' ? null : n;
    }
    if (pendingVehicleCategoryId !== undefined) {
      vehicleBody.vehicle_category_id =
        pendingVehicleCategoryId === '' ? null : parseInt(pendingVehicleCategoryId, 10);
    }
    if (pendingAlternativeFuelId !== undefined) {
      vehicleBody.alternative_fuel_id =
        pendingAlternativeFuelId === '' ? null : parseInt(pendingAlternativeFuelId, 10);
    }
    if (pendingUsageStatusId !== undefined) {
      vehicleBody.usage_status_id =
        pendingUsageStatusId === '' ? null : parseInt(pendingUsageStatusId, 10);
    }
    if (pendingBusinessResponsibilityId !== undefined) {
      vehicleBody.business_responsibility_id =
        pendingBusinessResponsibilityId === ''
          ? null
          : parseInt(pendingBusinessResponsibilityId, 10);
    }
    if (pendingReferenceNumber !== undefined) {
      const r = pendingReferenceNumber.trim();
      vehicleBody.reference_number = r === '' ? null : r;
    }
    if (pendingPlacement !== undefined) {
      const p = pendingPlacement;
      vehicleBody.placement_address = p.address.trim() === '' ? null : p.address.trim();
      vehicleBody.placement_postal_code = p.postalCode.trim() === '' ? null : p.postalCode.trim();
      vehicleBody.placement_city = p.city.trim() === '' ? null : p.city.trim();
      vehicleBody.placement_latitude = p.latitude;
      vehicleBody.placement_longitude = p.longitude;
    }
    if (pendingCharging !== undefined) {
      const c = pendingCharging;
      vehicleBody.battery_capacity_kwh = c.batteryCapacityKwh;
      vehicleBody.charging_efficiency = c.chargingEfficiency;
      vehicleBody.home_latitude = c.homeLatitude;
      vehicleBody.home_longitude = c.homeLongitude;
      vehicleBody.home_radius_m = c.homeRadiusM;
      vehicleBody.home_address = c.homeAddress;
      vehicleBody.work_latitude = c.workLatitude;
      vehicleBody.work_longitude = c.workLongitude;
      vehicleBody.work_radius_m = c.workRadiusM;
      vehicleBody.work_address = c.workAddress;
    }
    if (Object.keys(vehicleBody).length > 0) {
      const response = await api.put(`/api/vehicles/${writeVehicleId}`, vehicleBody);
      if (!response.data.success) {
        throw new Error(response.data.error || 'Kunde inte spara fordonsändringar');
      }
    }

    setPendingImei(undefined);
    setPendingNotes(undefined);
    setPendingOrganizationId(undefined);
    setPendingVehicleCategoryId(undefined);
    setPendingAlternativeFuelId(undefined);
    setPendingUsageStatusId(undefined);
    setPendingBusinessResponsibilityId(undefined);
    setPendingReplacesVehicleRegistration(undefined);
    setPendingReferenceNumber(undefined);
    setPendingPlacement(undefined);
    setPendingCharging(undefined);
    await fetchDetails();
  };

  const saveChanges = async () => {
    if (!vehicle?.id || !hasChanges) return;

    setSaveError(null);

    const orgChanging =
      pendingOrganizationId !== undefined &&
      pendingOrganizationId !== (vehicle.organizationId ?? '');

    if (orgChanging) {
      const oid = parseInt(String(pendingOrganizationId), 10);
      if (Number.isNaN(oid) || oid <= 0) {
        setSaveError('Ogiltig organisation');
        return;
      }
      let curTop = String(vehicle.topNodeOrganizationId ?? '');
      if (!curTop && vehicle.organizationId) {
        try {
          const r = await api.get<{ success: boolean; data?: { topNodeOrganizationId?: string | null } }>(
            `/api/organizations/${vehicle.organizationId}/top-node`
          );
          if (r.data.success && r.data.data?.topNodeOrganizationId != null) {
            curTop = String(r.data.data.topNodeOrganizationId);
          }
        } catch {
          /* ignorera */
        }
      }
      try {
        const topRes = await api.get<{ success: boolean; data?: { topNodeOrganizationId?: string | null } }>(
          `/api/organizations/${oid}/top-node`
        );
        const targetTop = String(topRes.data?.data?.topNodeOrganizationId ?? '');
        const isCross = Boolean(curTop && targetTop && curTop !== targetTop);
        setOrgMovePending({ targetOrganizationId: oid, isCrossTop: isCross });
        return;
      } catch (err: unknown) {
        console.error('Error resolving top nodes:', err);
        setSaveError('Kunde inte avgöra toppnod för vald organisation');
        return;
      }
    }

    setIsSaving(true);
    try {
      await runVehicleSavePipeline({});
    } catch (err: unknown) {
      console.error('Error saving changes:', err);
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
          : undefined;
      setSaveError(
        typeof msg === 'string' && msg.trim()
          ? msg
          : err instanceof Error
            ? err.message
            : 'Ett fel uppstod vid sparande'
      );
    } finally {
      setIsSaving(false);
    }
  };

  // Debounce device search
  useEffect(() => {
    if (showDeviceDropdown && vehicle?.id) {
      const timer = setTimeout(() => {
        fetchAvailableDevices(deviceSearch);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [deviceSearch, showDeviceDropdown, vehicle?.id, fetchAvailableDevices]);

  useEffect(() => {
    if (!vehicle?.id) {
      setPickupPlacements([]);
      setPickupPlacementsLoaded(false);
      return;
    }
    let cancelled = false;
    setPickupPlacementsLoaded(false);
    (async () => {
      try {
        const list = await vehiclesService.getVehiclePickupPlacements(String(vehicle.id));
        if (!cancelled) {
          setPickupPlacements(list);
          setPickupPlacementsLoaded(true);
        }
      } catch {
        if (!cancelled) {
          setPickupPlacements([]);
          setPickupPlacementsLoaded(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [vehicle?.id]);

  useEffect(() => {
    if (pickupPlacementsLoaded && activeTab === 'booking' && pickupPlacements.length === 0) {
      setActiveTab('general');
    }
  }, [pickupPlacementsLoaded, pickupPlacements.length, activeTab]);

  const showBookingTab = pickupPlacementsLoaded && pickupPlacements.length > 0;

  return {
    // Loading states
    isLoading,
    isRefreshing,
    error,
    
    // Data
    vehicle,
    carInfo,
    odometer,
    meta,
    
    // Tab
    activeTab,
    setActiveTab,
    pickupPlacements,
    showBookingTab,
    
    // Device assignment
    availableDevices,
    deviceSearch,
    setDeviceSearch,
    isLoadingDevices,
    showDeviceDropdown,
    setShowDeviceDropdown,
    selectDevice,
    
    // Changes
    pendingImei,
    pendingNotes,
    setPendingNotes,
    pendingOrganizationId,
    setPendingOrganizationId,
    vehicleCategories,
    isLoadingVehicleCategories,
    pendingVehicleCategoryId,
    setPendingVehicleCategoryId,
    alternativeFuels,
    isLoadingAlternativeFuels,
    pendingAlternativeFuelId,
    setPendingAlternativeFuelId,
    usageStatuses,
    isLoadingUsageStatuses,
    pendingUsageStatusId,
    setPendingUsageStatusId,
    pendingBusinessResponsibilityId,
    setPendingBusinessResponsibilityId,
    pendingReplacesVehicleRegistration,
    setPendingReplacesVehicleRegistration,
    pendingReferenceNumber,
    setPendingReferenceNumber,
    pendingPlacement,
    setPendingPlacement,
    placementFromVehicle,
    pendingCharging,
    setPendingCharging,
    chargingFromVehicle,
    hasChanges,
    isSaving,
    saveError,
    setSaveError,
    saveChanges,
    resetChanges,
    orgMovePending,
    confirmOrgMove,
    cancelOrgMove,
    
    // Odometer
    isEditingOdometer,
    setIsEditingOdometer,
    pendingInitialOdometerKm,
    setPendingInitialOdometerKm,
    pendingCurrentOdometerKm,
    setPendingCurrentOdometerKm,
    isSavingOdometer,
    saveOdometer,
    cancelOdometerEdit,
    
    // Specifications
    expandedCategories,
    toggleCategory,
    
    // flinkey (gammalt textinput-flöde)
    flinkeyBoxIdInput,
    setFlinkeyBoxIdInput,
    isSavingFlinkey,
    attachFlinkey,
    detachFlinkey,
    showAssignModal,
    setShowAssignModal,
    assignUserId,
    setAssignUserId,
    assignValidFrom,
    setAssignValidFrom,
    assignValidUntil,
    setAssignValidUntil,
    assignFlinkey,
    isAssigningFlinkey,

    // flinkey box (nytt dropdown-flöde från flinkey_boxes-tabellen)
    linkedFlinkeyBox,
    availableFlinkeyBoxes,
    isLoadingFlinkeyBoxes,
    flinkeyListLoadError,
    fetchFlinkeyBox,
    linkFlinkeyBox,
    unlinkFlinkeyBox,

    showFlinkeyBoxDropdown,
    setShowFlinkeyBoxDropdown,
    flinkeyBoxSearch,
    setFlinkeyBoxSearch,

    // Actions
    fetchDetails,
    fetchAvailableDevices,
  };
};
